import { Box, Flex, Text, Button, Spinner, Center, Show, useMediaQuery } from '@chakra-ui/react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react'
import useMetaTags from 'react-metatags-hook'
import ShareButton from '../../components/ShareButton'
import { ROUTES } from '../../routes';
import { MetaTags } from '@olagg/db-types';
import OlaggMarkdown from '@olagg/ui-kit/OlaggMarkdown';
import { useAuthStore } from '@olagg/store'
import useNews from './hooks/useNews';
import RelatedNews from './components/RelatedNews';
import Tag from './components/Tag';
import { analytics } from '../../analytics';
import { NrTracker } from '../../components/NrTracker';
import { useReferrals } from '../Referrals/hooks/useReferrals';
import UserAvatar from '../../components/UserAvatar';
import { PiEyeBold, PiThumbsDownBold, PiThumbsUpBold } from 'react-icons/pi';
import moment from 'moment/moment.js'
import 'moment/min/locales'
import { AdTrackingContext } from '@/contexts/adTrackingContext';
import { useNrTranslation } from '@/utils/useNrTranslation';
import { useTranslation } from 'react-i18next';

const Single = () => {
  const { tNr } = useNrTranslation();
  const { i18n, t } = useTranslation();
  const { fcb, fbp } = useContext(AdTrackingContext)
  const { loggedIn } = useAuthStore()
  const [metas, setMetas] = useState<MetaTags | null>(null);
  const location = useLocation();
  const pathname = location.pathname;
  const pathnameExtract = pathname.split(`${ROUTES.NEWS}/`)[1]
  const { ActionType, generateReferrerId } = useReferrals();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { singleNews: news, getBySlug, vote, singleRelatedNews, isLoading } = useNews()
  const referrerId = generateReferrerId(ActionType.VOTE_NEWS);

  const { totalUpVotes, totalDownVotes, myVote } = news?.votes || { totalUpVotes: 0, totalDownVotes: 0 }

  const [isLargerThan768] = useMediaQuery('(min-width: 768px)')

  const preview = searchParams.get('preview')?.toLowerCase() === 'true';
  const update = searchParams.get('update')?.toLowerCase() === 'true';

  useMetaTags({
    title: metas?.title,
    description: metas?.description,
    charset: 'utf8',
    lang: 'es',
    metas: [
      { name: 'twitter:title', content: metas?.title },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:url', content: metas?.url },
      { name: 'twitter:description', content: metas?.description },
      { name: 'twitter:image', content: metas?.image },
      { name: 'twitter:creator', content: '@olagg' },
      { name: 'twitter:label1', content: 'Autor' },
      { name: 'twitter:data1', content: metas?.author },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: metas?.title },
      { property: 'og:description', content: metas?.description },
      { property: 'og:site_name', content: 'Ola GG' },
      { property: 'og:url', content: metas?.url },
      { property: 'og:image', content: metas?.image },
      { property: 'og:image:secure_url', content: metas?.image },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'fb:app_id', content: `${import.meta.env.VITE_FACEBOOK_CLIENT_ID}` },
      { ...(preview && { name: 'robots', content: 'noindex' }) },
    ],
  }, [metas])

  useEffect(() => {
    getBySlug(pathnameExtract, preview, update)
  }, [pathnameExtract])

  useEffect(() => {
    if (news) {
      setMetas({
        title: tNr('title', news),
        description: `${tNr('lead', news)} ${t('News.single.metas.description')}`,
        url: `${import.meta.env.VITE_BASE_URL}/novedades/${news?.slug}`,
        image: tNr('coverImage', news),
        author: `${news.author?.firstName} ${news.author?.lastName}`
      });
    }
  }, [news, pathnameExtract]);

  const handleVote = async (value) => {
    if (!loggedIn) navigate(`${ROUTES.LOGIN}?redirect=/novedades/${news.slug}`);
    await vote(value)

    const analyticsData = {
      content_type: "news",
      content_id: news?.id,
      author_user_id: news?.author?.externalUserId,
      tags: news?.tags && Array.from(news.tags, tag => tag.name),
      ...(value === 1 && { total_upvotes: Number(totalUpVotes) + (myVote === value ? -value : value) }),
      ...(value === -1 && { total_downvotes: Number(totalDownVotes) + (myVote === value ? -value : value) }),
    };

    if (fcb != null) analyticsData.fbc = fcb
    if (fbp != null) analyticsData.fbp = fbp

    analytics()?.track(
      `${value === 1 ? "News UpVoted" : "News DownVoted"}`,
      analyticsData,
      { user_agent: navigator.userAgent }
    );

  }

  if (preview && !loggedIn) return (
    <Flex justifyContent='center' alignItems='center' w='full' h='200px'>
      <Text color='white'>{t('News.single.previewLogin')}</Text>
    </Flex>
  )

  if (isLoading) return (
    <Center h="100vh" color="white">
      <Spinner color="white" size="lg" />
    </Center>
  )

  const newsTypeColors = {
    NOVEDADES: '#710DEA',
    GUIDE: '#F08200',
  }

  const newsTypeText = {
    NOVEDADES: t('News.single.newsTypeText.news'),
    GUIDE: t('News.single.newsTypeText.guide'),
  }

  const userData = () => {
    return {
      userId: news.olaUser?.id || news.author?.externalUserId,
      name: news.olaUser?.name || `${news.author?.firstName} ${news.author?.lastName}`,
      image: news.olaUser?.avatar || news.author?.profileImage,
      authorRole: news.author?.role,
    }
  }

  return (
    <>
      <NrTracker id={news?.uid} />
      {news ? (
        <Flex
          justifyContent='space-between'
          flexWrap='wrap'
          pt={5}
          pb={20}
          h={'full'}
          color='#ffffff'
          fontWeight='600'
          position='relative'
        >
          <Flex
            direction={'column'}
            p={{ base: '16px', md: '48px' }}
            w='100%'
            bgColor='#1f1736'
            css={{
              '& h1': {
                fontSize: '45px',
                fontWeight: '700'
              },
            }}
          >
            <Flex alignSelf='flex-start' sx={{
              textTransform: 'uppercase',
              color: 'white',
              padding: '3px 8px',
              backgroundColor: newsTypeColors['NOVEDADES'],
              height: '20px',
              fontSize: '12px',
              fontWeight: '600',
              lineHeight: '16px',
              borderRadius: '6px',
              marginBottom: '8px'
            }}
            >
              {newsTypeText[news?.entityName.toUpperCase()]}
            </Flex>

            <Text fontSize={'36px'} fontWeight='700' as={'h1'} lineHeight={'100%'}>{tNr('title', news)}</Text>

            <Show below='md'>
              <Flex direction={'row'} alignItems='center' gap='24px' w='100%' mt='16px'>
                <Flex>
                  <Text color='#d2d1d7' fontSize='14px' mt='8px' fontWeight={'400'}>{moment(news.publicationDate).locale(i18n.language).fromNow()}</Text>
                </Flex>

                <Flex direction={'row'} alignItems='center' mt='6px' gap='6px' ml='auto'>
                  <PiEyeBold size={'16px'} color='white' />
                  <Text color='white' fontSize='14px' mt='3px' fontWeight={'700'}>{news.views || 0}</Text>
                </Flex>
              </Flex>
            </Show>

            <Flex direction={'row'} alignItems='center' gap='24px' w='100%' mt='24px'>
              <UserAvatar
                userId={userData().userId}
                name={userData().name}
                fontSize={{ base: '16px', md: '24px' }}
                fontWeight={'600'}
                avatarHeight={{ base: '24px', md: '38px' }}
                avatarWidth={{ base: '24px', md: '38px' }}
                image={userData().image}
                authorRole={userData().authorRole}
              />

              <Show above='md'>
                <Flex>
                  <Text color='#d2d1d7' fontSize='16px' mt='8px' fontWeight={'400'}>{moment(news.publicationDate).locale(i18n.language).fromNow()}</Text>
                </Flex>

                <Flex direction={'row'} alignItems='center' mt='6px' gap='6px'>
                  <PiEyeBold size={'18px'} color='white' />
                  <Text color='white' fontSize='16px' mt='2px' fontWeight={'700'}>{news.views || 0}</Text>
                </Flex>
              </Show>

              {!preview && (
                <Flex ml='auto' gap='8px'>
                  <Flex bgColor='#433c56' alignItems='center' gap={{ base: '6px', md: '8px' }} p={{ base: '0 6px', md: '8px' }} borderRadius={'6px'} cursor='pointer' onClick={() => handleVote(1)}>
                    <PiThumbsUpBold size='14px' color='white' /> <Text fontSize='16px' position='relative'>{totalUpVotes}</Text>
                  </Flex>
                  <Flex bgColor='#433c56' alignItems='center' gap={{ base: '6px', md: '8px' }} p={{ base: '0 6px', md: '8px' }} borderRadius={'6px'} cursor='pointer' onClick={() => handleVote(-1)}>
                    <PiThumbsDownBold size='14px' color='white' /> <Text fontSize='16px' position='relative'>{totalDownVotes}</Text>
                  </Flex>

                  <ShareButton
                    variant='filledGradient' border='initial'
                    title={t('News.single.shareButton.title')}
                    description={t('News.single.shareButton.description')}
                    link={`${import.meta.env.VITE_BASE_URL}/${i18n.language}${ROUTES.NEWS}/${news.slug}${referrerId}`}
                    animate={!!referrerId}
                    size={isLargerThan768 ? 'inherit' : 'small'}
                  />
                </Flex>
              )}

            </Flex>

          </Flex>
          <Box
            mt='7'
            px='7'
            w={{ 'base': '100%', 'lg': '65%' }}
            borderRight={{ 'base': 'none', 'md': '1px solid #343847' }}
          >
            <OlaggMarkdown>
              {tNr('body', news)}
            </OlaggMarkdown>

            <Flex direction='row' alignItems={'center'} gap='8px' justifyContent={'center'} mb='24px'>
              <Flex gap='8px'>
                <Flex bgColor='#433c56' alignItems='center' gap={{ base: '6px', md: '8px' }} p={{ base: '0 6px', md: '8px' }} borderRadius={'6px'} cursor='pointer' onClick={() => handleVote(1)}>
                  <PiThumbsUpBold size='14px' color='white' /> <Text fontSize='16px' position='relative'>{totalUpVotes}</Text>
                </Flex>
                <Flex bgColor='#433c56' alignItems='center' gap={{ base: '6px', md: '8px' }} p={{ base: '0 6px', md: '8px' }} borderRadius={'6px'} cursor='pointer' onClick={() => handleVote(-1)}>
                  <PiThumbsDownBold size='14px' color='white' /> <Text fontSize='16px' position='relative'>{totalDownVotes}</Text>
                </Flex>
              </Flex>

              <ShareButton
                variant='filledGradient' border='initial'
                title={t('News.single.shareButton.title')}
                description={t('News.single.shareButton.description')}
                link={`${import.meta.env.VITE_BASE_URL}/${i18n.language}${ROUTES.NEWS}/${news.slug}${referrerId}`}
                animate={!!referrerId}
                size={isLargerThan768 ? 'inherit' : 'small'}
                shareText={false}
              />
            </Flex>

            {!loggedIn && (<Flex
              flexDirection='column'
              alignItems='center'
              w='full'
              bg='linear-gradient(90deg, #4E10FF 0%, #AB17DF 100%)'
              p='10'
            >
              <Text fontSize='18px' fontWeight='400'>{t('News.single.newsletter.title')}</Text>
              <Text fontSize='30px' fontWeight='700'>{t('News.single.newsletter.subscribe')}</Text>
              <Text fontSize='15px' fontWeight='400'>{t('News.single.newsletter.findOut')}</Text>
              <Button
                mt='5'
                px='10'
                variant='filledGradient'
                onClick={() => window.location.href = 'https://olagg.io/login?redirect=https%3A%2F%2Folagg.io%2Fnovedades%3Futm_source%3Dnewsroom%26utm_medium%3Dbanner%26utm_campaign%3Dlog-in'}
              >
                {t('News.single.newsletter.clickHere')}
              </Button>
            </Flex>)}
          </Box>
          <Box
            mt={{ 'base': '10', 'lg': '7' }}
            px='7'
            w={{ 'base': '100%', 'lg': '35%' }}
            position='relative'
          >
            <Box position='sticky' top='100px'>
              {singleRelatedNews != null && singleRelatedNews.length != 0 && (
                <RelatedNews title={t('News.single.relatedNews.lead')} relatedNews={singleRelatedNews} />
              )}
              {news.tags.length != 0 && (
                <Tag tags={news.tags} />
              )}
            </Box>
          </Box>
        </Flex>
      ) : (
        <Center h="100vh" color="white">
          <Text>{t('News.single.notFound')}</Text>
        </Center>
      )}
    </>
  )
}

export default Single
